import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─────────────────────────────────────────────────────────────
// Instance Axios
// ─────────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Lecture du token depuis le store centralisé (madjeo_auth) ou legacy
function getAccessToken() {
  try {
    const stored = JSON.parse(localStorage.getItem('madjeo_auth'));
    if (stored?.accessToken) return stored.accessToken;
  } catch { /* ignore */ }
  return localStorage.getItem('access_token') || null;
}

function getRefreshToken() {
  try {
    const stored = JSON.parse(localStorage.getItem('madjeo_auth'));
    if (stored?.refreshToken) return stored.refreshToken;
  } catch { /* ignore */ }
  return localStorage.getItem('refresh_token') || null;
}

// ── Request : injecter le Bearer token ───────────────────────
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response : refresh JWT automatique (queue pattern) ───────
let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  );
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing    = true;

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      window.dispatchEvent(new Event('madjeo:logout'));
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post(
        `${BASE_URL}/api/v1/auth/token/refresh/`,
        { refresh: refreshToken }
      );
      const newAccess  = data.access;
      const newRefresh = data.refresh; // présent si ROTATE_REFRESH_TOKENS = True
      // Mettre à jour le store (access + refresh pour éviter le blacklist)
      try {
        const stored = JSON.parse(localStorage.getItem('madjeo_auth')) || {};
        stored.accessToken = newAccess;
        if (newRefresh) stored.refreshToken = newRefresh;
        localStorage.setItem('madjeo_auth', JSON.stringify(stored));
      } catch { /* ignore */ }
      processQueue(null, newAccess);
      original.headers.Authorization = `Bearer ${newAccess}`;
      return api(original);
    } catch (err) {
      processQueue(err);
      window.dispatchEvent(new Event('madjeo:logout'));
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;

// =============================================================
// AUTH  /api/v1/auth/
// =============================================================
export const authApi = {
  signup:          (data)           => api.post('/api/v1/auth/signup/', data),
  login:           (data)           => api.post('/api/v1/auth/login/', data),
  tokenRefresh:    (refresh)        => api.post('/api/v1/auth/token/refresh/', { refresh }),
  getProfile:      ()               => api.get('/api/v1/auth/profile/'),
  updateProfile:   (data)           => api.patch('/api/v1/auth/profile/', data),
  changePassword:  (data)           => api.post('/api/v1/auth/change-password/', data),
  // Activation par lien e-mail (url directe — construit côté composant)
  activateUrl:     (uid, token)     => `/api/v1/auth/activate/${uid}/${token}/`,
  passwordReset:        (data)       => api.post('/api/v1/auth/password/reset/', data),
  passwordResetConfirm: (data)      => api.post('/api/v1/auth/password/reset/confirm/', data),
  googleLogin:     (data)           => api.post('/api/v1/auth/google/', data),
  facebookLogin:   (data)           => api.post('/api/v1/auth/facebook/', data),
};

// =============================================================
// FILES  /api/v1/files/
// =============================================================
export const filesApi = {
  upload:          (formData)       => api.post('/api/v1/files/storage/upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  list:            ()               => api.get('/api/v1/files/storage/files/'),
  remove:          (id)             => api.delete(`/api/v1/files/storage/files/${id}/`),

  shpPreview:      (formData)       => api.post('/api/v1/files/shp/preview/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  shpSave:         (formData)       => api.post('/api/v1/files/shp/save/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60_000,
  }),

  excelAnalyze:    (formData)       => api.post('/api/v1/files/excel/analyze/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  excelSave:       (formData)        => api.post('/api/v1/files/excel/save/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  pdfExtract:      (formData)       => api.post('/api/v1/files/pdf/extract/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60_000,
  }),
};

// =============================================================
// TOPOGRAPHIE  /api/v1/topo/
// =============================================================
export const topoApi = {
  uploadGsi:             (formData) => api.post('/api/v1/topo/upload-gsi/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getStationsR:          (data)     => api.post('/api/v1/topo/getStationsR/', data),
  setV0:                 (data)     => api.post('/api/v1/topo/SetV0/', data),
  getAutoPath:           (data)     => api.post('/api/v1/topo/getAutoPath/', data),
  calculateCheminement:  (data)     => api.post('/api/v1/topo/calculateCheminement/', data),
  uploadDxf:             (formData) => api.post('/api/v1/topo/upload-dxf/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  exportGsi:             (data)     => api.post('/api/v1/topo/exportGsi/', data),
};

// =============================================================
// GÉODÉSIE  /api/v1/geodesie/
// =============================================================
export const geodesieApi = {
  pointsGeodesiquesByRadius: (params) =>
    api.get('/api/v1/geodesie/points-geodesiques/', { params }),
  getBbox:  (data)       => api.post('/api/v1/geodesie/getBbox/', data),
  stats:    ()           => api.get('/api/v1/geodesie/admin/stats/'),
  list:     (params)     => api.get('/api/v1/geodesie/admin/points/', { params: { page_size: params?.size, ...params } }),
  detail:   (id)         => api.get(`/api/v1/geodesie/admin/points/${id}/`),
  update:   (id, data)   => api.patch(`/api/v1/geodesie/admin/points/${id}/`, data),
  remove:      (id)      => api.delete(`/api/v1/geodesie/admin/points/${id}/`),
  removeMany:  (ids)     => api.delete('/api/v1/geodesie/admin/points/bulk-delete/', { data: { ids } }),
};

// =============================================================
// LABORDE  /api/v1/laborde/
// =============================================================
export const labordeApi = {
  sept7Params:    (data) => api.post('/api/v1/laborde/7parametres/', data),
  convertCoords:  (data) => api.post('/api/v1/laborde/convert-coords/', data),
  distance:       (data) => api.post('/api/v1/laborde/distance/', data),
  surface:        (data) => api.post('/api/v1/laborde/surface/', data),
};

// =============================================================
// DESSIN / DXF  /api/v1/draw/
// =============================================================
export const drawApi = {
  exportDxf: (data) => api.post('/api/v1/draw/dxf/', data),
};

// =============================================================
// PAIEMENTS  /api/v1/payments/
// =============================================================
export const paymentsApi = {
  price:         ()                      => api.get('/api/v1/payments/price/'),
  check:         (pointId)               => api.get(`/api/v1/payments/check/${pointId}/`),
  myPoints:      ()                      => api.get('/api/v1/payments/my-points/'),
  initiate:      (data)                  => api.post('/api/v1/payments/initiate/', data),
  stripeIntent:  (data)                  => api.post('/api/v1/payments/stripe/create-intent/', data),
  stripeConfirm: (purchaseId)            => api.post(`/api/v1/payments/stripe/confirm/${purchaseId}/`),
  momoConfirm:   (purchaseId, phone)     => api.post(`/api/v1/payments/momo/confirm/${purchaseId}/`, { phone }),
  mvolaInitiate:   (purchaseId, phone)   => api.post(`/api/v1/payments/mvola/initiate/${purchaseId}/`, { phone }),
  mvolaStatus:     (purchaseId)          => api.get(`/api/v1/payments/mvola/status/${purchaseId}/`),
  airtelInitiate:  (purchaseId, phone)   => api.post(`/api/v1/payments/airtel/initiate/${purchaseId}/`, { phone }),
  airtelStatus:    (purchaseId)          => api.get(`/api/v1/payments/airtel/status/${purchaseId}/`),
  orangeInitiate:  (purchaseId)          => api.post(`/api/v1/payments/orange/initiate/${purchaseId}/`),
  orangeStatus:    (purchaseId, orderId) => api.get(`/api/v1/payments/orange/status/${purchaseId}/`, { params: { order_id: orderId } }),
  pointDetail:     (pointId)             => api.get(`/api/v1/payments/point-detail/${pointId}/`),
};

// =============================================================
// SIG — Limites administratives  /api/v1/sig/
// =============================================================
export const sigApi = {
  stats:   ()                   => api.get('/api/v1/sig/stats/'),
  list:    (type, params)       => api.get(`/api/v1/sig/${type}/`, { params }),
  geojson: (type, params)       => api.get(`/api/v1/sig/${type}/geojson/`, { params }),
  detail:  (type, id)           => api.get(`/api/v1/sig/${type}/${id}/`),
  update:  (type, id, data)     => api.patch(`/api/v1/sig/${type}/${id}/`, data),
  remove:  (type, id)           => api.delete(`/api/v1/sig/${type}/${id}/`),
  preview: (fd)                 => api.post('/api/v1/sig/preview/', fd),
  import:  (fd)                 => api.post('/api/v1/sig/import/', fd),
};
